import React, { useCallback, useMemo, useState } from "react";
import {
  Box,
  Button,
  FormField,
  Header,
  Modal,
  RadioGroup,
  RadioGroupProps,
  SpaceBetween,
} from "@amzn/awsui-components-console";

import type { IplPreScreenData, IplQualificationAnswers } from "./types";

interface IplPreScreenProps {
  /**
   * Invoked when the user submits the IPL pre-screen with valid data.
   * Only called when qualification passes.
   */
  onPreScreenSubmit: (data: IplPreScreenData) => void;
}

const YES_NO_OPTIONS: RadioGroupProps.RadioButtonDefinition[] = [
  { label: "Yes", value: "yes" },
  { label: "No", value: "no" },
];

interface QualificationQuestion {
  key: keyof IplQualificationAnswers;
  label: string;
}

const QUALIFICATION_QUESTIONS: QualificationQuestion[] = [
  {
    key: "productCausedInjuries",
    label:
      "Is the claim involving a product that allegedly caused bodily injuries and/or property damages?",
  },
  {
    key: "requestingItemRemoval",
    label: "Is the claimant only requesting that the item be removed from sale?",
  },
  {
    key: "sharingNegativeFeedback",
    label: "Is the claimant only seeking to share their negative feedback?",
  },
  {
    key: "refundRequestOnly",
    label: "Is the claim related to a refund request only?",
  },
  {
    key: "seekingAdditionalCompensation",
    label: "Is the claimant seeking additional compensation for their claim?",
  },
  {
    key: "deliveryIssueRelated",
    label: "Is the claim related to a delivery issue?",
  },
];

const EMPTY_QUALIFICATION_ANSWERS: IplQualificationAnswers = {
  productCausedInjuries: null,
  requestingItemRemoval: null,
  sharingNegativeFeedback: null,
  refundRequestOnly: null,
  seekingAdditionalCompensation: null,
  deliveryIssueRelated: null,
};

/**
 * Returns `true` when the qualification answers represent a valid IPL claim:
 * Q1 (productCausedInjuries) = yes, Q5 (seekingAdditionalCompensation) = yes,
 * and all other questions = no.
 */
function isQualificationValid(answers: IplQualificationAnswers): boolean {
  return (
    answers.productCausedInjuries === "yes" &&
    answers.requestingItemRemoval === "no" &&
    answers.sharingNegativeFeedback === "no" &&
    answers.refundRequestOnly === "no" &&
    answers.seekingAdditionalCompensation === "yes" &&
    answers.deliveryIssueRelated === "no"
  );
}

/**
 * Returns `true` when every qualification question has been answered.
 */
function allQuestionsAnswered(answers: IplQualificationAnswers): boolean {
  return Object.values(answers).every((v) => v !== null);
}

/**
 * IPL pre-screen rendered by `JsonFormsClaimIntakeApp` for the `ipl` LOB.
 *
 * Shows six Yes/No qualification questions. If answers pass validation
 * (Q1=yes, Q5=yes, rest=no) the schema is resolved. Otherwise an
 * invalid-claim modal is displayed.
 */
function IplPreScreen({ onPreScreenSubmit }: IplPreScreenProps): JSX.Element {
  const [qualificationAnswers, setQualificationAnswers] = useState<IplQualificationAnswers>(
    EMPTY_QUALIFICATION_ANSWERS
  );
  const [showInvalidModal, setShowInvalidModal] = useState(false);

  const handleQualificationChange = useCallback(
    (key: keyof IplQualificationAnswers, value: string) => {
      setQualificationAnswers((prev) => ({ ...prev, [key]: value }));
    },
    []
  );

  const handleSubmit = useCallback(() => {
    if (isQualificationValid(qualificationAnswers)) {
      onPreScreenSubmit({
        claimPath: "ipl",
        atozCountry: null,
        qualificationAnswers,
      });
    } else {
      setShowInvalidModal(true);
    }
  }, [qualificationAnswers, onPreScreenSubmit]);

  const isSubmitDisabled = useMemo(
    () => !allQuestionsAnswered(qualificationAnswers),
    [qualificationAnswers]
  );

  return (
    <Box padding="l" data-testid="ipl-pre-screen">
      <SpaceBetween size="m" direction="vertical">
        <Header variant="h2">IPL claim intake</Header>

        {QUALIFICATION_QUESTIONS.map((q) => (
          <FormField key={q.key} label={q.label} data-testid={`ipl-pre-screen-${q.key}-form-field`}>
            <RadioGroup
              data-testid={`ipl-pre-screen-${q.key}-radio`}
              items={YES_NO_OPTIONS}
              value={qualificationAnswers[q.key]}
              onChange={({ detail }) => handleQualificationChange(q.key, detail.value)}
            />
          </FormField>
        ))}

        <Button
          variant="primary"
          disabled={isSubmitDisabled}
          onClick={handleSubmit}
          data-testid="ipl-pre-screen-submit-button"
        >
          Continue
        </Button>
      </SpaceBetween>

      <Modal
        visible={showInvalidModal}
        onDismiss={() => setShowInvalidModal(false)}
        header="Invalid IPL Claim"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setShowInvalidModal(false)}>
                Cancel
              </Button>
            </SpaceBetween>
          </Box>
        }
        data-testid="ipl-pre-screen-invalid-modal"
      >
        <span>
          Based on your answer(s), this does not appear to be a product liability claim. Please
          click cancel and update if you answered in error. Please reach out via this{" "}
          <a
            href="https://t.corp.amazon.com/create/templates/ab7c3b78-7569-4425-bd8f-d54ff377c46e"
            target="_blank"
            rel="noopener noreferrer"
          >
            ticket
          </a>{" "}
          if you need additional assistance.
        </span>
      </Modal>
    </Box>
  );
}

export default IplPreScreen;
